import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { RouteComponentProps } from 'react-router';
import Metadata from '@om-fe/core/components/metadata/metadata';
import { LiveMatchesContent } from '../components/match-content-tabs/live-matches-content';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { createWidgetsSportRouteParser } from '@om-fe/core/core/routing/widgets/widgets-routes';
import { RouteParser, SportRouteParams } from '@om-fe/widgets/dist/common/routing/routing.constants';
import { Spinner } from '@om-fe/widgets/dist/components/spinner/spinner';
import { SportsNavModel, SportsNavStoreData, UseSportsNavStore } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks';
import { useEMRouting } from '@om-fe/core/core/routing/with-routing';
import { useNavLiveSportsTopicStoreCreator } from '../components/match-content-tabs/live-matches.hooks';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { FavoritesStoreState } from '@om-fe/core/components/favorite-button/favorite-state.constants';
import { FavoritesStoreState as FavoritesStoreStateV2 } from '@om-fe/core/components/favorite-events/favorite-state.constants';
import getSlice from '@om-fe/core/utils/get-slice';
import { favoriteSlice } from '@om-fe/core/components/favorite-button/favorite-state.store';
import { DisciplineId } from '../components/match-content-tabs/match-content-tabs.constants';
import RedirectTo from '@om-fe/core/components/redirect-to/redirect-to';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { useReady } from '@om-fe/store';
import { EmptyMatchMessage } from "../components/empty-match-message/empty-match-message";
import { withSelectionBetslipData, WithSelectionBetslipDataModel } from '@om-fe/widgets/dist/widgets/betslip/with-betslip-selection-data/with-betslip-selection-data';
import { DeepLink } from '@om-fe/core/utils/deep-link';

export type LiveSportsPublicProps = RouteComponentProps<{
    readonly liveDisciplineId: string;
    readonly liveDisciplineName: string;
    readonly liveLocationId: string;
    readonly bettingOfferIds?: string;
    readonly stake?: string;
}>

interface LiveSportsStoreProps {
    readonly favorites: number;
    readonly favoritesLoading: boolean;
    readonly userID?: number;
    readonly sessionLoading: boolean;
}

type LiveSportsProps = LiveSportsPublicProps & LiveSportsStoreProps & WithSelectionBetslipDataModel;

function LiveSportsComponent(
    {
        favorites,
        favoritesLoading,
        userID,
        sessionLoading,
        match: { params: { liveDisciplineId, liveDisciplineName, liveLocationId, bettingOfferIds, stake } },
        addSelection,
        setSingleSelectionStake,
        setMasterStake,
    }: LiveSportsProps,
): React.ReactElement {
    const { gettext } = useI18NContext();
    const metadataText: string = gettext('Live Sports Betting');
    const { getFullPath } = useEMRouting();
    const baseRouteParser: ((params: SportRouteParams) => string) = useMemo(() => {
        return createWidgetsSportRouteParser(gettext, getFullPath);
    }, [getFullPath, gettext]);

    const routeParser: RouteParser<SportRouteParams> = useCallback((params: SportRouteParams) => {
        return baseRouteParser({
            ...params,
            hasLive: true,
        });
    }, [baseRouteParser]);

    const useNavLiveSportsStore: UseSportsNavStore = useNavLiveSportsTopicStoreCreator(
        false,
        favorites,
    );

    const { liveList } = useNavLiveSportsStore.getState();
    const ready: boolean = useReady(useNavLiveSportsStore);
    const loading: boolean = !ready || sessionLoading || (!!userID && favoritesLoading);

    const redirectItem: SportsNavModel | undefined = useNavLiveSportsStore(useCallback((
        state: SportsNavStoreData,
    ): SportsNavModel | undefined => {
        return !liveDisciplineId
        || (
            liveDisciplineId === DisciplineId.Favorites && !loading && !favorites
        )
        || (
            liveDisciplineId
            && liveDisciplineId !== DisciplineId.Favorites
            && ready
            && !state.liveList.includes(liveDisciplineId)
        )
            ? state.items[state.liveList[0]]
            : undefined;
    }, [favorites, liveDisciplineId, loading, ready]));

    useEffect(() => {
        DeepLink({ bettingOfferIds, stake, id: undefined, addSelection, setSingleSelectionStake, setMasterStake });
    }, [addSelection, bettingOfferIds, setMasterStake, setSingleSelectionStake, stake]);

    if (!ready || (liveDisciplineId === DisciplineId.Favorites && loading)) {
        return (
            <Spinner />
        );
    }

    if(ready && liveList.length === 0) {
        return (
            <EmptyMatchMessage message={gettext('There are currently no live bets available!')} />
        );
    }

    if (redirectItem) {
        return (
            <RedirectTo
                routeName={RouteNames.liveSports}
                routeParams={{
                    liveDisciplineName: redirectItem.name,
                    liveDisciplineId: redirectItem.id,
                    liveLocationName: gettext('All'),
                    liveLocationId: 0
                }}
            />
        );
    }

    return (
        <>
            <Metadata
                title={metadataText}
                keywords={metadataText}
                description={metadataText}
            />
            <LiveMatchesContent
                useStore={useNavLiveSportsStore}
                sportRoute={routeParser}
                hasSearch
                sportId={liveDisciplineId}
                sportName={liveDisciplineName}
                genericId={liveLocationId}
            />
        </>
    );
}

export const LiveSports: FC<LiveSportsProps> = compose(
    withSelectionBetslipData<LiveSportsProps>(),
    connect((state: AppState): LiveSportsStoreProps => {
        const favorites: FavoritesStoreState | FavoritesStoreStateV2 | undefined = getSlice<FavoritesStoreState | FavoritesStoreStateV2>(state, favoriteSlice);

        return {
            favorites: favorites ? Object.keys(favorites.favoriteMatchItems).length : 0,
            favoritesLoading: !favorites?.ready,
            userID: state.session.info?.userID,
            sessionLoading: state.session.loading,
        };
    },
))(LiveSportsComponent);
export default LiveSports;
